import React, { useEffect, useState, useCallback } from 'react';

/**
 * Inbox view inside the Auto Apply page. Mirrors AIApply's split-pane
 * layout: list on the left, selected message body on the right. Pulls
 * messages straight from /api/inbox/messages (which proxies Nylas).
 *
 * Rendered only when the parent has confirmed `nylas.connected === true`,
 * so we don't try to fetch when there's no grant.
 */
export default function InboxView({ apiBaseUrl }) {
  const [messages, setMessages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedBody, setSelectedBody] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingBody, setLoadingBody] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'job-related'
  const [iframeHeight, setIframeHeight] = useState(400);

  const token = () => localStorage.getItem('token');

  // Listen for height updates posted by the email iframe so the frame
  // grows to fit the full email — no inner scrollbar, page scroll instead.
  useEffect(() => {
    const handler = (e) => {
      if (e.data && e.data.type === 'terview-email-height' && typeof e.data.height === 'number') {
        setIframeHeight(Math.max(200, Math.min(e.data.height + 24, 20000)));
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Reset to a sensible default when switching emails so we don't briefly
  // show the previous email's height before the new one reports back.
  useEffect(() => { setIframeHeight(400); }, [selectedId]);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/api/inbox/messages?limit=50`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingList(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openMessage = useCallback(async (msgId) => {
    setSelectedId(msgId);
    setSelectedBody(null);
    setLoadingBody(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/inbox/messages/${encodeURIComponent(msgId)}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSelectedBody(data);
      // Optimistically mark read locally so the badge drops without re-fetch.
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, unread: false } : m));
      // Best-effort persist read state on Nylas's side.
      fetch(`${apiBaseUrl}/api/inbox/messages/${encodeURIComponent(msgId)}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ unread: false }),
      }).catch(() => {});
    } catch (err) {
      setSelectedBody({ subject: 'Error', bodyHtml: `<p style="color:#f87171">Failed to load: ${err.message}</p>` });
    } finally {
      setLoadingBody(false);
    }
  }, [apiBaseUrl]);

  // Job-related = sender domain matches a known ATS or contains the word
  // "careers", "talent", "recruit", etc. Cheap heuristic — the LLM
  // classifier is Day 2.
  const isJobRelated = (m) => {
    const email = (m.from?.email || '').toLowerCase();
    const subject = (m.subject || '').toLowerCase();
    const blob = email + ' ' + subject;
    return /greenhouse-mail\.io|lever\.co|workable\.com|ashbyhq\.com|smartrecruiters|myworkdayjobs|icims|careers|talent|recruit|application|job|interview/.test(blob);
  };

  const visible = messages.filter(m =>
    filter === 'all'         ? true :
    filter === 'unread'      ? m.unread :
    filter === 'job-related' ? isJobRelated(m) :
    true
  );

  const formatDate = (epochSeconds) => {
    if (!epochSeconds) return '';
    const d = new Date(epochSeconds * 1000);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Deterministic color per sender — same email always gets the same hue.
  const avatarColor = (seed = '') => {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    const hue = h % 360;
    return `hsl(${hue}, 55%, 45%)`;
  };
  const avatarInitial = (m) => {
    const s = m.from?.name || m.from?.email || '?';
    return s.trim().charAt(0).toUpperCase() || '?';
  };

  return (
    <div style={{
      marginTop: 16, padding: 0, borderRadius: 12,
      border: '1px solid rgba(124,58,237,0.25)',
      background: 'rgba(20,10,40,0.4)',
      overflow: 'hidden',
      display: 'grid',
      gridTemplateColumns: '380px minmax(0, 1fr)',
      minHeight: 600,
      maxWidth: '100%',
    }}>
      {/* ── LEFT: list ── */}
      <div style={{
        borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Filter strip */}
        <div style={{
          padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <label style={{ color: 'rgba(224,163,255,0.7)', fontSize: 12, fontWeight: 600 }}>Filter:</label>
          <select value={filter} onChange={e => setFilter(e.target.value)} style={{
            padding: '5px 10px', borderRadius: 6,
            background: 'rgba(255,255,255,0.05)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.15)', fontSize: 12,
          }}>
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="job-related">Job-related</option>
          </select>
          <button
            onClick={fetchList}
            disabled={loadingList}
            style={{
              marginLeft: 'auto', padding: '5px 10px', borderRadius: 6,
              background: 'transparent', color: '#a78bfa',
              border: '1px solid rgba(167,139,250,0.4)', cursor: 'pointer', fontSize: 12,
            }}>
            {loadingList ? '⏳' : '🔄'}
          </button>
        </div>

        {/* List */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loadingList && messages.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
              Loading…
            </div>
          )}
          {error && (
            <div style={{ padding: 20, color: '#f87171', fontSize: 13 }}>
              Error: {error}
            </div>
          )}
          {!loadingList && visible.length === 0 && !error && (
            <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
              No messages.
            </div>
          )}
          {visible.map(m => {
            const isActive = m.id === selectedId;
            const seed = m.from?.email || m.from?.name || '';
            return (
              <button
                key={m.id}
                onClick={() => openMessage(m.id)}
                style={{
                  display: 'flex', width: '100%', textAlign: 'left',
                  alignItems: 'flex-start', gap: 10,
                  padding: '12px 14px',
                  borderTop: 'none',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  borderLeft: isActive ? '3px solid #7c3aed' : '3px solid transparent',
                  borderRight: 'none',
                  background: isActive ? 'rgba(124,58,237,0.12)' : 'transparent',
                  color: '#fff',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: avatarColor(seed),
                  color: '#fff', fontSize: 13, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginTop: 2,
                  position: 'relative',
                }}>
                  {avatarInitial(m)}
                  {m.unread && (
                    <span style={{
                      position: 'absolute', top: -2, right: -2,
                      width: 10, height: 10, borderRadius: '50%',
                      background: '#7c3aed', border: '2px solid rgba(20,10,40,1)',
                    }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    marginBottom: 3, gap: 8,
                  }}>
                    <div style={{
                      fontSize: 13, fontWeight: m.unread ? 700 : 500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      color: m.unread ? '#fff' : 'rgba(255,255,255,0.75)',
                    }}>
                      {m.from?.name || m.from?.email || '(unknown)'}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
                      {formatDate(m.date)}
                    </div>
                  </div>
                  <div style={{
                    fontSize: 12.5,
                    fontWeight: m.unread ? 600 : 400,
                    color: m.unread ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.65)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    marginBottom: 2,
                  }}>
                    {m.subject}
                  </div>
                  <div style={{
                    fontSize: 11.5, color: 'rgba(255,255,255,0.4)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {m.snippet}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── RIGHT: body ── */}
      <div style={{ padding: '20px 24px', overflow: 'visible', minWidth: 0 }}>
        {!selectedId && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', color: 'rgba(255,255,255,0.4)', textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 14 }}>No email selected</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Click a message on the left to view it.</div>
          </div>
        )}
        {selectedId && loadingBody && (
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>Loading…</div>
        )}
        {selectedId && selectedBody && !loadingBody && (
          <>
            <h2 style={{ margin: '0 0 14px', fontSize: 20, color: '#fff', fontWeight: 600, lineHeight: 1.3 }}>
              {selectedBody.subject}
            </h2>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              marginBottom: 16, paddingBottom: 14,
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                background: avatarColor(selectedBody.from?.email || selectedBody.from?.name || ''),
                color: '#fff', fontSize: 16, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {(selectedBody.from?.name || selectedBody.from?.email || '?').trim().charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, color: '#fff', fontWeight: 600, lineHeight: 1.3 }}>
                  {selectedBody.from?.name || selectedBody.from?.email}
                </div>
                {selectedBody.from?.email && selectedBody.from?.name && (
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                    &lt;{selectedBody.from.email}&gt;
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
                {formatDate(selectedBody.date)}
              </div>
            </div>
            {/* Render email HTML in a sandboxed iframe so its fixed-pixel
                tables/images can't push the outer column wider. The iframe
                also fully isolates email CSS/scripts from the app. The
                small inline script measures content height and posts it to
                the parent so the iframe grows to fit — no inner scrollbar.
                sandbox="allow-scripts" runs the resize script but keeps
                the iframe in an opaque origin (no access to app cookies). */}
            <div style={{
              borderRadius: 10,
              overflow: 'hidden',
              background: '#fafafa',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
            }}>
              <iframe
                key={selectedId}
                title={selectedBody.subject || 'Email'}
                sandbox="allow-scripts allow-popups"
                srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
                  html,body{margin:0;padding:18px 22px;background:#fafafa;color:rgba(0,0,0,0.82);font-family:system-ui,-apple-system,sans-serif;font-size:14px;line-height:1.55;word-break:break-word;}
                  img{max-width:100%!important;height:auto!important;}
                  table{max-width:100%!important;}
                  a{color:#6d28d9;}
                </style></head><body>${selectedBody.bodyHtml || ''}<script>
                  (function(){
                    function post(){
                      var h=Math.max(document.body.scrollHeight,document.documentElement.scrollHeight,document.body.offsetHeight,document.documentElement.offsetHeight);
                      parent.postMessage({type:'terview-email-height',height:h},'*');
                    }
                    post();
                    window.addEventListener('load',post);
                    if(window.ResizeObserver){new ResizeObserver(post).observe(document.body);}
                    document.querySelectorAll('img').forEach(function(i){if(!i.complete){i.addEventListener('load',post);i.addEventListener('error',post);}});
                  })();
                </\u0073cript></body></html>`}
                style={{
                  width: '100%',
                  height: iframeHeight,
                  border: 'none',
                  background: '#fafafa',
                  display: 'block',
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
